import React, { useEffect, useState } from 'react';
import { Col, Input, Row } from 'antd';
import { BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import Invoice from '@app/components/apps/common/Invoice';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, message, Popconfirm } from 'antd';
import {
  handleFUWorkflowStatusChange,
  handleLabWorkflowStatusChange,
  handlePRWorkflowStatusChange,
} from '@app/utils/WFforInvoice';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { updateInvoice } from '@app/api/labs.api';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import { MakeInvoicePayment } from '@app/api/finances.api';

const InvoicePage: React.FC = () => {
  const location = useLocation();
  const {
    id,
    pr_code,
    invoiceId,
    invoice_number,
    invoice_status,
    status,
    labStatus,
    fu_status,
    wfActions,
    lab_code,
    PRstatus,
    ImpStatus,
    model,
    amount,
    total,
  } = location.state || {};
  const [currentBreadcrumName, setCurrentBreadcrumName] = useState<string>('Invoice');
  const { setBreadcrumb, clearHeaderButtons, addHeaderButton } = useHeader();
  const navigate = useNavigate();
  const isLab = localStorage.getItem('isLab') === 'true';
  const invoiceType = localStorage.getItem('invoiceType');
  const handleErrorMessages = useApiErrorHandler();
  const isInvoiceModel = model === 'INV';
  const [isPayableInvoice, setIsPayableInvoice] = useState<boolean>(false);
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = (storedUser?.usertype ?? '').toUpperCase();

  const invoiceRoutes = [
    {
      path: '/all-invoices',
      breadcrumbName: 'Invoices',
    },
    {
      path: '/view-invoice',
      breadcrumbName: invoice_number || 'Invoice',
    },
  ];

  const routes = isInvoiceModel
    ? invoiceRoutes
    : invoiceType === 'FU'
    ? [
        {
          path: '/follow-ups',
          breadcrumbName: 'Follow Ups',
        },
        {
          path: '/view-follow-up',
          breadcrumbName: pr_code,
          state: { id: id },
        },
        {
          path: '/view-invoice',
          breadcrumbName: currentBreadcrumName,
        },
      ]
    : isLab
    ? [
        {
          path: '/lab-regular-tests',
          breadcrumbName: 'Lab Regular Tests',
        },
        {
          path: '/view-lab-task',
          breadcrumbName: lab_code,
          state: { id: id },
        },
        {
          path: '/view-invoice',
          breadcrumbName: currentBreadcrumName,
        },
      ]
    : [
        {
          path: '/product-registrations',
          breadcrumbName: 'Product Registrations',
        },
        {
          path: '/view-product-registrations',
          breadcrumbName: pr_code,
          state: { id: id },
        },
        {
          path: '/view-invoice',
          breadcrumbName: currentBreadcrumName,
        },
      ].filter((route) => route);

  useEffect(() => {
    if (model === 'INV') {
      setCurrentBreadcrumName(invoice_number || 'Invoice');
    }
    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);
    addHeaderButton(
      'Download PDF',
      <BaseButton type="primary" size="small" onClick={handleDownloadPDF}>
        Download Invoice
      </BaseButton>,
    );
    if ((labStatus === 'PENDING_PAYMENT' || userType === 'CASHIER') && !isPayableInvoice) {
      addHeaderButton(
        'Zero Payment',
        <Popconfirm
          title={<span style={{ color: 'black' }}>Are you sure you want to validate this Invoice?</span>}
          onConfirm={async () => {
            try {
              await updateInvoice(invoiceId);
              message.success('Invoice approved as zero invoice.');
              handlePostPaymentStatusChange();
            } catch (err) {
              handleErrorMessages(err);
              console.error('Zero invoice update error:', err);
            }
          }}
          okText="Yes"
          cancelText="No"
        >
          <BaseButton
            size="small"
            style={{
              backgroundColor: 'white',
              color: '#2c3e91',
              border: '1px solid #2c3e91',
            }}
          >
            Validate Invoice
          </BaseButton>
        </Popconfirm>,
      );

      if (isPayableInvoice) {
        addHeaderButton(
          'Make Payment',
          <BaseButton type="primary" size="small" onClick={handleMakePayment}>
            Make Payment
          </BaseButton>,
        );
      }
    }
    if ((PRstatus === 'PENDING_DOCUMENT_PAYMENT' || PRstatus === 'PENDING_PAYMENT') && isPayableInvoice) {
      addHeaderButton(
        'Make Payment',
        <BaseButton type="primary" size="small" onClick={handleMakePayment}>
          Make Payment
        </BaseButton>,
      );
    }
    if ((invoice_status === 'PENDING' || invoice_status === 'PARTIALLY_PAID') && isPayableInvoice) {
      addHeaderButton(
        'Make Payment',
        <BaseButton type="primary" size="small" onClick={handleMakePayment}>
          Make Payment
        </BaseButton>,
      );
    }
    if (fu_status === 'PENDING_PAYMENT' && isPayableInvoice) {
      addHeaderButton(
        'Make Payment',
        <BaseButton type="primary" size="small" onClick={handleMakePayment}>
          Make Payment
        </BaseButton>,
      );
    }

    addHeaderButton(
      'Close',
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <BaseButton
          type="default"
          size="small"
          danger
          onClick={() => {
            if (isLab) {
              navigate('/view-lab-task', { state: { id, activeTab: '8' } });
            } else if (model === 'INV') {
              navigate('/all-invoices');
            } else if (invoiceType === 'FU') {
              navigate('/view-follow-up', { state: { id, activeTab: '9' } });
            } else {
              navigate('/view-product-registrations', { state: { id, activeTab: '8' } });
            }
          }}
        >
          Close
        </BaseButton>
      </div>,
    );

    return () => {
      setBreadcrumb(undefined);
      clearHeaderButtons();
    };
  }, [
    setBreadcrumb,
    clearHeaderButtons,
    addHeaderButton,
    currentBreadcrumName,
    routes,
    status,
    model,
    invoice_number,
    isPayableInvoice,
  ]);

  const handleDownloadPDF = () => {
    const invoiceElement = document.getElementById('invoice-content');

    if (!invoiceElement) {
      console.error('Invoice content not found!');
      return;
    }

    html2canvas(invoiceElement, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const marginTop = 15;
      const marginBottom = 10;

      const usablePageHeight = pageHeight - marginTop - marginBottom;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const scaleRatio = pageWidth / canvasWidth;
      const scaledCanvasHeight = canvasHeight * scaleRatio;
      const sliceHeightInPx = usablePageHeight / scaleRatio;

      let position = 0;
      let pageIndex = 0;

      while (position < canvasHeight) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.min(sliceHeightInPx, canvasHeight - position);

        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, position, canvasWidth, pageCanvas.height, 0, 0, canvasWidth, pageCanvas.height);

          const imgData = pageCanvas.toDataURL('image/png');
          if (pageIndex > 0) pdf.addPage();

          const imgHeightInMm = pageCanvas.height * scaleRatio;

          pdf.addImage(imgData, 'PNG', 0, marginTop, pageWidth, imgHeightInMm);
        }

        position += sliceHeightInPx;
        pageIndex++;
      }

      pdf.save(`invoice-${invoice_number}.pdf`);
    });
  };

  const handleMakePayment = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = (storedUser?.usertype ?? '').toUpperCase();

    const modalInstance = Modal.info({
      title: 'Select Payment Method',
      content: renderPaymentButtons(),
      okButtonProps: { style: { display: 'none' } },
      maskClosable: true,
      focusTriggerAfterClose: false,
    });

    function renderPaymentButtons() {
      return (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          {userType !== 'CUSTOMER' && (
            <BaseButton
              onClick={() => {
                modalInstance.update({
                  content: renderCashInput(),
                  title: 'Confirm Payment',
                });
              }}
            >
              Cash Payment
            </BaseButton>
          )}
          <BaseButton
            onClick={async () => {
              modalInstance.destroy();
              try {
                await updateInvoice(invoiceId);
                message.success('Card payment confirmed.');
                handlePostPaymentStatusChange();
              } catch (err) {
                handleErrorMessages(err);
                console.error('Invoice update error:', err);
              }
            }}
          >
            Card Payment
          </BaseButton>
        </div>
      );
    }

    function renderCashInput() {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
          <p> Pay LKR {total}?</p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <BaseButton
              size="small"
              onClick={() => {
                // Cancel and go back to payment buttons
                modalInstance.update({
                  content: renderPaymentButtons(),
                  title: 'Confirm Payment',
                });
              }}
            >
              Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              size="small"
              onClick={() => {
                if (!total) {
                  message.error('Invoice total undefined');
                  return;
                }

                // Show confirmation modal for entered amount
                Modal.confirm({
                  title: 'Confirm Cash Payment',
                  content: `Pay LKR ${total}?`,
                  okText: 'Confirm & Pay',
                  cancelText: 'Cancel',
                  okButtonProps: { size: 'small' },
                  cancelButtonProps: { size: 'small' },
                  onOk: async () => {
                    modalInstance.destroy();
                    // try {
                    //   if (isInvoiceModel) {
                    //     await MakeInvoicePayment(invoiceId, 'CASH', total);
                    //     message.success(`Cash payment of ${total} confirmed.`);
                    //   } else {
                    //     await updateInvoice(invoiceId);
                    //     message.success(`Cash payment of ${total} confirmed.`);
                    //   }
                    //   handlePostPaymentStatusChange();
                    // } catch (err) {
                    //   handleErrorMessages(err);
                    //   console.error('Cash payment error:', err);
                    // }

                    try {
                      await MakeInvoicePayment(invoiceId, 'CASH', total);

                      // await updateInvoice(invoiceId);

                      message.success(`Cash payment of ${total} confirmed.`);
                      if (invoiceType === 'FU') {
                        navigate('/view-follow-up', { state: { id } });
                        localStorage.removeItem('invoiceType');
                        return;
                      }
                      handlePostPaymentStatusChange();
                    } catch (err) {
                      handleErrorMessages(err);
                      console.error('Cash payment error:', err);
                    }
                  },
                  onCancel: () => {
                    // Do nothing; user can re-enter amount
                  },
                });
              }}
            >
              Pay
            </BaseButton>
          </div>
        </div>
      );
    }
  };

  const handlePostPaymentStatusChange = async () => {
    if (id && labStatus && Array.isArray(wfActions) && wfActions.length > 0) {
      await handleLabWorkflowStatusChange(id, labStatus, wfActions, (newStatus) => {
        console.log('Task status updated to:', newStatus);
        navigate(`/view-lab-task`, { state: { id } });
      });
    } else if (id && fu_status && Array.isArray(wfActions) && wfActions.length > 0) {
      await handleFUWorkflowStatusChange(id, fu_status, wfActions, (newStatus) => {
        console.log('FU status updated to:', newStatus);

        navigate(`/view-follow-up`, { state: { id } });
      });
    } else if (id && PRstatus && Array.isArray(wfActions) && wfActions.length > 0) {
      await handlePRWorkflowStatusChange(id, PRstatus, wfActions, (newStatus) => {
        console.log('PR status updated to:', newStatus);

        navigate(`/view-product-registrations`, { state: { id } });
      });
    } else if (isInvoiceModel) {
      navigate('/all-invoices');
    } else {
      console.warn('Missing required data:', { id, labStatus, PRstatus, wfActions });
    }
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Invoice
            id={invoiceId}
            setCurrentBreadcrumName={setCurrentBreadcrumName}
            setIsPayable={setIsPayableInvoice}
          />
        </Col>
      </Row>
    </>
  );
};

export default InvoicePage;
